import { useEffect, useState } from "react"
import { Navigate, useLocation } from "react-router-dom"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"

export function PrivateRoute({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const location = useLocation()
  const { toast } = useToast()

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Verificar sessão atual
        const { data: { session } } = await supabase.auth.getSession()
        
        if (!session) {
          setIsAuthenticated(false)
          toast({
            variant: "destructive",
            title: "Acesso negado",
            description: "Por favor, faça login para continuar.",
          })
          return
        }

        // Verificar se o token não está expirado
        const tokenExpiration = session.expires_at
        if (tokenExpiration && tokenExpiration * 1000 < Date.now()) {
          await supabase.auth.signOut()
          setIsAuthenticated(false)
          toast({
            variant: "destructive",
            title: "Sessão expirada",
            description: "Por favor, faça login novamente.",
          })
          return
        }

        // Verificar se o perfil existe
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', session.user.id)
          .single()

        if (profileError || !profile) {
          await supabase.auth.signOut()
          setIsAuthenticated(false)
          toast({
            variant: "destructive",
            title: "Acesso negado",
            description: "Perfil não encontrado. Entre em contato com o administrador.",
          })
          return
        }
        
        setIsAuthenticated(true)
      } catch (error) {
        console.error("Erro ao verificar autenticação:", error)
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()

    // Listener para mudanças na autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        setIsAuthenticated(false)
      } else if (event === 'SIGNED_IN') {
        setIsAuthenticated(true)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [toast])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return isAuthenticated ? (
    children
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  )
}